/**
 * 다양한 상태 정보를 시각적으로 표시하는 카드 컴포넌트
 * 
 * 이 컴포넌트는 크롤링 작업, 지식베이스 처리 등 다양한 상태 정보를 일관된 UI로 표시합니다.
 * 상태에 따라 색상과 아이콘이 자동으로 변경되며, 진행 상태 표시줄과 상세 정보 표시를 지원합니다.
 * 
 * @param {Object} props
 * @param {string} props.title - 카드 제목
 * @param {string} props.status - 상태 값 (completed, processing, failed, pending 등)
 * @param {string} props.message - 상태에 대한 설명 메시지
 * @param {Object} props.details - 추가 세부 정보를 포함하는 객체 (키-값 쌍으로 표시됨)
 * @param {boolean} props.isLoading - 로딩 중 여부 (진행 표시줄 표시 여부)
 * @param {number|null} props.progressPercent - 진행률 (0-100)
 * 
 * @example
 * <StatusCard
 *   title="웹 크롤링 작업"
 *   status="processing"
 *   message="웹 페이지 데이터를 수집 중입니다"
 *   details={{ 
 *     "URL 수": "15", 
 *     "수집 완료": "7", 
 *     "시작 시간": "2023-10-15 14:30" 
 *   }}
 *   isLoading={true}
 *   progressPercent={45}
 * />
 */

import React from 'react';
import { Card, CardContent, Typography, Box, Chip, LinearProgress } from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import ErrorIcon from '@mui/icons-material/Error';
import PendingIcon from '@mui/icons-material/Pending';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

const StatusCard = ({ title, status, message, details = {}, isLoading = false, progressPercent = null }) => {
  // 상태에 따른 색상 설정
  const getStatusColor = () => {
    switch (status) {
      case 'completed':
      case 'processed':
      case 'ready':
      case 'active':
        return 'success';
      case 'failed':
      case 'error':
      case 'cancelled':
        return 'error';
      case 'pending':
      case 'queued':
        return 'warning';
      case 'crawling':
      case 'processing':
      case 'building':
        return 'info';
      default:
        return 'default';
    }
  };

  // 상태에 따른 아이콘
  const getStatusIcon = () => {
    switch (status) {
      case 'completed':
      case 'processed':
      case 'ready':
      case 'active':
        return <DoneIcon fontSize="small" />;
      case 'failed':
      case 'error':
      case 'cancelled':
        return <ErrorIcon fontSize="small" />;
      case 'pending':
      case 'queued':
        return <PendingIcon fontSize="small" />;
      case 'crawling':
      case 'processing':
      case 'building':
        return <HourglassEmptyIcon fontSize="small" />;
      default:
        return null;
    }
  };

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      {isLoading && <LinearProgress />}
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" component="div">
            {title}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Chip
              label={status}
              color={getStatusColor()}
              size="small"
              icon={getStatusIcon()}
            />
            {/* 진행률 % 표시 */}
            {isLoading && progressPercent !== null && (
              <Typography variant="body2" sx={{ ml: 1 }}>
                {progressPercent}%
              </Typography>
            )}
          </Box>
        </Box>
        <Typography color="text.secondary" gutterBottom>
          {message}
        </Typography>
        {Object.entries(details).length > 0 && (
          <Box sx={{ mt: 2 }}>
            {Object.entries(details).map(([key, value]) => (
              <Box key={key} sx={{ display: 'flex', mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1, minWidth: '120px' }}>
                  {key}:
                </Typography>
                <Typography variant="body2">
                  {value !== null && value !== undefined ? value.toString() : '-'}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default StatusCard; 